import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Plus, Pin, PinOff, Trash2, Edit2, Save, X } from 'lucide-react';
import { 
  ImplementationObservation, 
  CreateObservationDTO,
  ImplementationPhase,
  PHASE_LABELS
} from '@/types/customerSupport';
import { useAuth } from '@/contexts/AuthContext';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface ImplementationObservationsProps {
  implementationId: number;
  observations: ImplementationObservation[];
  onObservationCreated: (observation: ImplementationObservation) => void;
  onObservationUpdated: (observation: ImplementationObservation) => void;
  onObservationDeleted: (observationId: number) => void;
}

const ImplementationObservations: React.FC<ImplementationObservationsProps> = ({
  implementationId,
  observations,
  onObservationCreated,
  onObservationUpdated,
  onObservationDeleted
}) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [newContent, setNewContent] = useState('');
  const [newPhase, setNewPhase] = useState<ImplementationPhase | ''>('');
  const [editContent, setEditContent] = useState('');

  const handleCreate = async () => {
    if (!newContent.trim()) {
      toast({
        title: 'Erro',
        description: 'O conteúdo da observação é obrigatório',
        variant: 'destructive'
      });
      return;
    }

    try {
      const { customerSupportService } = await import('@/services/customerSupportService');
      const observation = await customerSupportService.createObservation(implementationId, {
        content: newContent,
        phase: newPhase || undefined,
        is_pinned: false
      });
      
      onObservationCreated(observation);
      setNewContent('');
      setNewPhase('');
      setIsAdding(false);
      
      toast({
        title: 'Sucesso',
        description: 'Observação criada com sucesso'
      });
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error.message || 'Erro ao criar observação',
        variant: 'destructive'
      });
    }
  };

  const handleUpdate = async (observationId: number) => {
    if (!editContent.trim()) {
      toast({
        title: 'Erro',
        description: 'O conteúdo da observação é obrigatório',
        variant: 'destructive'
      });
      return;
    }

    try {
      const { customerSupportService } = await import('@/services/customerSupportService');
      const observation = await customerSupportService.updateObservation(implementationId, observationId, {
        content: editContent
      });
      
      onObservationUpdated(observation);
      setEditingId(null);
      setEditContent('');
      
      toast({
        title: 'Sucesso',
        description: 'Observação atualizada com sucesso'
      });
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error.message || 'Erro ao atualizar observação',
        variant: 'destructive'
      });
    }
  };

  const handleTogglePin = async (observation: ImplementationObservation) => {
    try {
      const { customerSupportService } = await import('@/services/customerSupportService');
      const updated = await customerSupportService.updateObservation(implementationId, observation.id, {
        is_pinned: !observation.is_pinned
      });
      
      onObservationUpdated(updated);
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error.message || 'Erro ao atualizar observação',
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (observationId: number) => {
    if (!confirm('Tem certeza que deseja deletar esta observação?')) {
      return;
    }

    try {
      const { customerSupportService } = await import('@/services/customerSupportService');
      await customerSupportService.deleteObservation(implementationId, observationId);
      
      onObservationDeleted(observationId);
      
      toast({
        title: 'Sucesso',
        description: 'Observação deletada com sucesso'
      });
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error.message || 'Erro ao deletar observação',
        variant: 'destructive'
      });
    }
  };

  const startEditing = (observation: ImplementationObservation) => {
    setEditingId(observation.id);
    setEditContent(observation.content);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditContent('');
  };

  // Separar observações fixadas e não fixadas
  const pinnedObservations = observations.filter(obs => obs.is_pinned);
  const unpinnedObservations = observations.filter(obs => !obs.is_pinned);

  return (
    <div className="space-y-4">
      {/* Botão Adicionar */}
      {!isAdding && (
        <Button onClick={() => setIsAdding(true)} variant="outline" className="w-full">
          <Plus className="h-4 w-4 mr-2" />
          Adicionar Observação
        </Button>
      )}

      {/* Formulário de Nova Observação */}
      {isAdding && (
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <Textarea
                placeholder="Digite sua observação..."
                value={newContent}
                onChange={(e) => setNewContent(e.target.value)}
                rows={4}
              />
              <div className="flex items-center gap-2">
                <select
                  value={newPhase}
                  onChange={(e) => setNewPhase(e.target.value as ImplementationPhase | '')}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Sem fase específica</option>
                  {Object.values(ImplementationPhase).map(phase => (
                    <option key={phase} value={phase}>
                      {PHASE_LABELS[phase]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleCreate} size="sm">
                  <Save className="h-4 w-4 mr-2" />
                  Salvar
                </Button>
                <Button onClick={() => {
                  setIsAdding(false);
                  setNewContent('');
                  setNewPhase('');
                }} variant="outline" size="sm">
                  <X className="h-4 w-4 mr-2" />
                  Cancelar
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Observações Fixadas */}
      {pinnedObservations.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-muted-foreground">Fixadas</h4>
          {pinnedObservations.map(observation => (
            <ObservationCard
              key={observation.id}
              observation={observation}
              isEditing={editingId === observation.id}
              editContent={editContent}
              onEditContentChange={setEditContent}
              onStartEdit={() => startEditing(observation)}
              onCancelEdit={cancelEditing}
              onSave={() => handleUpdate(observation.id)}
              onTogglePin={() => handleTogglePin(observation)}
              onDelete={() => handleDelete(observation.id)}
              canEdit={user?.id === observation.user_id}
            />
          ))}
        </div>
      )}

      {/* Observações Não Fixadas */}
      {unpinnedObservations.length > 0 && (
        <div className="space-y-2">
          {pinnedObservations.length > 0 && (
            <h4 className="text-sm font-semibold text-muted-foreground mt-4">Outras</h4>
          )}
          {unpinnedObservations.map(observation => (
            <ObservationCard
              key={observation.id}
              observation={observation}
              isEditing={editingId === observation.id}
              editContent={editContent}
              onEditContentChange={setEditContent}
              onStartEdit={() => startEditing(observation)}
              onCancelEdit={cancelEditing}
              onSave={() => handleUpdate(observation.id)}
              onTogglePin={() => handleTogglePin(observation)}
              onDelete={() => handleDelete(observation.id)}
              canEdit={user?.id === observation.user_id}
            />
          ))}
        </div>
      )}

      {observations.length === 0 && !isAdding && (
        <div className="text-center text-sm text-muted-foreground py-8">
          Nenhuma observação ainda. Adicione uma para começar!
        </div>
      )}
    </div>
  );
};

interface ObservationCardProps {
  observation: ImplementationObservation;
  isEditing: boolean;
  editContent: string;
  onEditContentChange: (content: string) => void;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSave: () => void;
  onTogglePin: () => void;
  onDelete: () => void;
  canEdit: boolean;
}

const ObservationCard: React.FC<ObservationCardProps> = ({
  observation,
  isEditing,
  editContent,
  onEditContentChange,
  onStartEdit,
  onCancelEdit,
  onSave,
  onTogglePin,
  onDelete,
  canEdit
}) => {
  return (
    <Card className={observation.is_pinned ? 'border-yellow-300 bg-yellow-50' : ''}>
      <CardContent className="pt-4">
        <div className="space-y-3">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-medium">{observation.user?.name || 'Usuário'}</span>
                {observation.phase && (
                  <Badge variant="outline" className="text-xs">
                    {PHASE_LABELS[observation.phase]}
                  </Badge>
                )}
              </div>
              <span className="text-xs text-muted-foreground">
                {format(new Date(observation.created_at), "dd 'de' MMMM 'às' HH:mm", { locale: ptBR })}
              </span>
            </div>
            <div className="flex gap-1">
              {canEdit && (
                <>
                  {observation.is_pinned ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={onTogglePin}
                      title="Desfixar"
                    >
                      <PinOff className="h-4 w-4" />
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={onTogglePin}
                      title="Fixar"
                    >
                      <Pin className="h-4 w-4" />
                    </Button>
                  )}
                  {!isEditing && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={onStartEdit}
                      title="Editar"
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive"
                    onClick={onDelete}
                    title="Deletar"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Content */}
          {isEditing ? (
            <div className="space-y-2">
              <Textarea
                value={editContent}
                onChange={(e) => onEditContentChange(e.target.value)}
                rows={4}
              />
              <div className="flex gap-2">
                <Button onClick={onSave} size="sm">
                  <Save className="h-4 w-4 mr-2" />
                  Salvar
                </Button>
                <Button onClick={onCancelEdit} variant="outline" size="sm">
                  <X className="h-4 w-4 mr-2" />
                  Cancelar
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-sm whitespace-pre-wrap">{observation.content}</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ImplementationObservations;

